function clamp(n, min, max) {
  const v = Number(n);
  if (!Number.isFinite(v)) return min;
  return Math.min(max, Math.max(min, v));
}

function safeText(s) {
  return String(s || '').trim();
}

function pickPalette(palette) {
  const arr = Array.isArray(palette) ? palette.filter(Boolean) : [];
  const norm = arr.map((c) => String(c).trim()).filter((c) => /^#[0-9a-fA-F]{6}$/.test(c));
  const uniq = Array.from(new Set(norm));
  if (uniq.length >= 2) return uniq.slice(0, 5);
  if (uniq.length === 1) return [uniq[0], '#111111', '#FFFFFF'];
  return ['#B77A2A', '#6E3E14', '#F5C55B'];
}

function pickColorSystem(colorSystem, mode = 'dark') {
  const cs = colorSystem && typeof colorSystem === 'object' ? colorSystem : null;
  const m = mode === 'light' ? 'light' : 'dark';
  const roles = cs?.[m]?.roles || cs?.roles || null;
  if (!roles) return null;
  const get = (k, fallback) => {
    const v = String(roles?.[k] || '').trim();
    return /^#[0-9a-fA-F]{6}$/.test(v) ? v : fallback;
  };
  return {
    primary: get('primary', '#B77A2A'),
    secondary: get('secondary', '#6E3E14'),
    accent: get('accent', '#F5C55B'),
    background: get('background', m === 'light' ? '#FFF7EC' : '#070503'),
    text: get('text', '#FFFFFF'),
    textMuted: get('textMuted', 'rgba(255,255,255,0.78)'),
  };
}

export function fontFamilyForBrand({ fontArabic, fontLatin, locale }) {
  const isArabic = String(locale || '').toLowerCase().startsWith('ar');
  if (isArabic) {
    if (fontArabic === 'noto_kufi_arabic') return "'Noto Kufi Arabic', 'Noto Sans Arabic', 'IBM Plex Sans Arabic', Tahoma, Arial, sans-serif";
    if (fontArabic === 'noto_sans_arabic') return "'Noto Sans Arabic', 'Noto Kufi Arabic', 'IBM Plex Sans Arabic', Tahoma, Arial, sans-serif";
    return "'IBM Plex Sans Arabic', 'Noto Sans Arabic', 'Noto Kufi Arabic', Tahoma, Arial, sans-serif";
  }
  if (fontLatin === 'inter') return "Inter, system-ui, -apple-system, 'Segoe UI', Arial, sans-serif";
  if (fontLatin === 'ibm_plex_sans') return "'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', Arial, sans-serif";
  return "system-ui, -apple-system, 'Segoe UI', Arial, sans-serif";
}

function makeGradient(ctx, w, h, palette) {
  const [a, b, c] = palette;
  const g = ctx.createLinearGradient(0, 0, w, h);
  g.addColorStop(0, a);
  g.addColorStop(0.6, b);
  g.addColorStop(1, c);
  return g;
}

function drawSoftNoise(ctx, w, h, alpha = 0.06) {
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = Math.floor(Math.random() * 18);
    d[i] = clamp(d[i] + n, 0, 255);
    d[i + 1] = clamp(d[i + 1] + n, 0, 255);
    d[i + 2] = clamp(d[i + 2] + n, 0, 255);
    d[i + 3] = clamp(d[i + 3] + Math.floor(255 * alpha), 0, 255);
  }
  ctx.putImageData(img, 0, 0);
}

async function loadImage(dataUrl) {
  const src = String(dataUrl || '').trim();
  if (!src) return null;
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function drawCenteredText(ctx, { text, x, y, maxWidth, fontSize, fontFamily, color, weight = 900 }) {
  ctx.fillStyle = color;
  ctx.font = `${weight} ${fontSize}px ${fontFamily}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const t = safeText(text);
  if (!t) return;

  // Fit by shrinking font size.
  let size = fontSize;
  for (let i = 0; i < 10; i++) {
    ctx.font = `${weight} ${size}px ${fontFamily}`;
    const m = ctx.measureText(t);
    if (m.width <= maxWidth) break;
    size = Math.max(10, Math.floor(size * 0.92));
  }
  ctx.font = `${weight} ${size}px ${fontFamily}`;
  ctx.fillText(t, x, y);
}

export async function renderHighlightCover({
  size = 1024,
  palette,
  colorSystem,
  colorMode = 'dark',
  label,
  iconDataUrl,
  iconBackdrop,
  fontFamily,
}) {
  const s = clamp(size, 256, 2048);
  const cs = pickColorSystem(colorSystem, colorMode);
  const colors = cs ? [cs.primary, cs.secondary, cs.accent] : pickPalette(palette);
  const textColor = cs?.text || 'rgba(255,255,255,0.96)';
  const canvas = document.createElement('canvas');
  canvas.width = s;
  canvas.height = s;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = makeGradient(ctx, s, s, colors);
  ctx.fillRect(0, 0, s, s);

  // Accent ring
  ctx.globalAlpha = 0.18;
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = Math.max(10, Math.round(s * 0.02));
  ctx.beginPath();
  ctx.arc(s / 2, s / 2, s * 0.42, 0, Math.PI * 2);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // Soft noise
  try {
    drawSoftNoise(ctx, s, s, 0.02);
  } catch {
    // ignore (some browsers block getImageData on huge canvases)
  }

  // Icon (optional)
  const img = await loadImage(iconDataUrl);
  if (img) {
    const box = Math.floor(s * 0.44);
    const x = Math.floor((s - box) / 2);
    const y = Math.floor(s * 0.22);

    const bd = iconBackdrop && typeof iconBackdrop === 'object' ? iconBackdrop : null;
    const bdColor = String(bd?.color || '').trim();
    const bdOpacity = Number(bd?.opacity);
    if (/^#[0-9a-fA-F]{6}$/.test(bdColor) && Number.isFinite(bdOpacity) && bdOpacity > 0) {
      ctx.save();
      ctx.globalAlpha = Math.min(1, Math.max(0, bdOpacity));
      ctx.fillStyle = bdColor;
      const cx = x + box / 2;
      const cy = y + box / 2;
      const r = box * 0.56;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    ctx.globalAlpha = 0.96;
    ctx.drawImage(img, x, y, box, box);
    ctx.globalAlpha = 1;
  }

  // Label
  drawCenteredText(ctx, {
    text: label,
    x: s / 2,
    y: s * 0.78,
    maxWidth: s * 0.84,
    fontSize: Math.floor(s * 0.08),
    fontFamily,
    color: textColor,
    weight: 900,
  });

  return canvas;
}

export async function renderProfileAvatar({
  size = 1024,
  palette,
  colorSystem,
  colorMode = 'dark',
  logoDataUrl,
  brandName,
  fontFamily,
}) {
  const s = clamp(size, 256, 2048);
  const cs = pickColorSystem(colorSystem, colorMode);
  const colors = cs ? [cs.primary, cs.secondary, cs.accent] : pickPalette(palette);
  const canvas = document.createElement('canvas');
  canvas.width = s;
  canvas.height = s;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = makeGradient(ctx, s, s, colors);
  ctx.fillRect(0, 0, s, s);

  // Vignette
  const rg = ctx.createRadialGradient(s / 2, s / 2, s * 0.1, s / 2, s / 2, s * 0.75);
  rg.addColorStop(0, 'rgba(0,0,0,0)');
  rg.addColorStop(1, 'rgba(0,0,0,0.28)');
  ctx.fillStyle = rg;
  ctx.fillRect(0, 0, s, s);

  const img = await loadImage(logoDataUrl);
  if (img) {
    const box = Math.floor(s * 0.58);
    const x = Math.floor((s - box) / 2);
    const y = Math.floor((s - box) / 2);
    ctx.globalAlpha = 0.95;
    ctx.drawImage(img, x, y, box, box);
    ctx.globalAlpha = 1;
  } else {
    drawCenteredText(ctx, {
      text: safeText(brandName).slice(0, 2).toUpperCase(),
      x: s / 2,
      y: s / 2,
      maxWidth: s * 0.9,
      fontSize: Math.floor(s * 0.28),
      fontFamily,
      color: 'rgba(255,255,255,0.96)',
      weight: 950,
    });
  }

  return canvas;
}

export async function canvasToPngDataUrl(canvas) {
  try {
    return canvas.toDataURL('image/png');
  } catch {
    return '';
  }
}

export function downloadDataUrl(dataUrl, filename) {
  const url = String(dataUrl || '').trim();
  if (!url) return;
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'brand-kit.png';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
